import { DashboardAntreanRapimSummary } from '../types';

type RapimQueueItemLike = {
  rapim_ulang?: boolean | null;
  dikembalikan_ke_rapim?: boolean | null;
};

export const EMPTY_RAPIM_QUEUE_SUMMARY: DashboardAntreanRapimSummary = {
  total: 0,
  baru_disetujui_warek: 0,
  dikembalikan_ke_rapim: 0,
};

const toSafeNumber = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
};

export const isRapimUlangItem = (item?: RapimQueueItemLike | null): boolean =>
  Boolean(item?.rapim_ulang || item?.dikembalikan_ke_rapim);

export const summarizeRapimQueueItems = (
  items?: RapimQueueItemLike[] | null
): DashboardAntreanRapimSummary => {
  const normalizedItems = Array.isArray(items) ? items : [];
  const dikembalikanKeRapim = normalizedItems.filter((item) => isRapimUlangItem(item)).length;
  const total = normalizedItems.length;

  return {
    total,
    baru_disetujui_warek: Math.max(0, total - dikembalikanKeRapim),
    dikembalikan_ke_rapim: dikembalikanKeRapim,
  };
};

export const normalizeRapimQueueSummary = (
  summary?: Partial<DashboardAntreanRapimSummary> | null
): DashboardAntreanRapimSummary => {
  const total = toSafeNumber(summary?.total);
  const dikembalikanKeRapim = Math.min(
    total,
    toSafeNumber(summary?.dikembalikan_ke_rapim)
  );
  const baruDisetujuiWarek = Math.min(
    total,
    summary?.baru_disetujui_warek != null
      ? toSafeNumber(summary?.baru_disetujui_warek)
      : Math.max(0, total - dikembalikanKeRapim)
  );

  return {
    total,
    baru_disetujui_warek: baruDisetujuiWarek,
    dikembalikan_ke_rapim: Math.min(
      total - baruDisetujuiWarek >= 0 ? total - baruDisetujuiWarek : total,
      dikembalikanKeRapim
    ),
  };
};

export const resolveRapimQueueSummary = (
  summary?: Partial<DashboardAntreanRapimSummary> | null,
  items?: RapimQueueItemLike[] | null
): DashboardAntreanRapimSummary => {
  const normalizedSummary = normalizeRapimQueueSummary(summary);
  if (normalizedSummary.total > 0) {
    return normalizedSummary;
  }

  return summarizeRapimQueueItems(items);
};

export const getRapimQueueAlertCopy = (
  summary: DashboardAntreanRapimSummary
): { title: string; description: string } => {
  if (summary.dikembalikan_ke_rapim > 0 && summary.baru_disetujui_warek === 0) {
    return {
      title: 'Ada Laporan Dikembalikan ke RAPIM',
      description: `Terdapat ${summary.total} laporan yang dikembalikan ke RAPIM karena hambatan tindak lanjut dan perlu segera dijadwalkan ulang untuk pembahasan lanjutan.`,
    };
  }

  if (summary.baru_disetujui_warek > 0 && summary.dikembalikan_ke_rapim === 0) {
    return {
      title: 'Ada Laporan Menunggu Rapat Pimpinan',
      description: `Terdapat ${summary.total} laporan yang sudah disetujui Wakil Rektor dan perlu segera dijadwalkan dalam Rapat Pimpinan untuk ditetapkan keputusan tindak lanjutnya.`,
    };
  }

  return {
    title: 'Ada Laporan Menunggu dan Kembali ke RAPIM',
    description: `Terdapat ${summary.total} laporan yang perlu penjadwalan RAPIM, terdiri dari ${summary.baru_disetujui_warek} laporan baru disetujui Wakil Rektor dan ${summary.dikembalikan_ke_rapim} laporan yang dikembalikan ke RAPIM untuk pembahasan ulang.`,
  };
};

export const getRapimQueueSectionCopy = (
  summary: DashboardAntreanRapimSummary
): string => {
  if (summary.dikembalikan_ke_rapim > 0 && summary.baru_disetujui_warek === 0) {
    return 'Laporan berikut dikembalikan ke RAPIM setelah tindak lanjut mengalami hambatan dan menunggu penjadwalan ulang untuk pembahasan lanjutan.';
  }

  if (summary.baru_disetujui_warek > 0 && summary.dikembalikan_ke_rapim === 0) {
    return 'Laporan berikut sudah disetujui oleh Wakil Rektor dan siap untuk dibahas dalam Rapat Pimpinan.';
  }

  return 'Daftar ini memuat laporan baru yang sudah disetujui Wakil Rektor serta laporan yang dikembalikan ke RAPIM untuk pembahasan ulang.';
};

export const getRapimQueueStatusHelper = (
  summary: DashboardAntreanRapimSummary
): string => {
  if (summary.dikembalikan_ke_rapim > 0 && summary.baru_disetujui_warek === 0) {
    return 'Laporan dikembalikan ke RAPIM setelah hambatan tindak lanjut dan menunggu penjadwalan ulang.';
  }

  if (summary.baru_disetujui_warek > 0 && summary.dikembalikan_ke_rapim === 0) {
    return 'Laporan telah disetujui Wakil Rektor dan menunggu penjadwalan Rapat Pimpinan.';
  }

  return 'Sebagian laporan baru disetujui Wakil Rektor dan sebagian lain dikembalikan ke RAPIM untuk pembahasan ulang.';
};
